import React, { useState } from "react";
import { Checkbox, Field, Text } from "@fluentui/react-components";
import { useParams } from "react-router-dom";
import { type ConnectionEditState, type ConnectionEditStateUpdater } from "@onetasklist/shared";
import {
  useGmailSourceSettingsText,
  useGmailSourceSettingsViewModel,
} from "@onetasklist/shared/sources/gmail";
import { getSourceSettingsApiAccessor } from "@onetasklist/shared/sources/source-settings";
import { useApi } from "@api/useApi";
import { useTranslation } from "react-i18next";
import { NewTaskAgeLimitField } from "./NewTaskAgeLimitField";
import { SourceSingleTagPicker } from "./components/SourceSingleTagPicker";
import { SettingsSectionCard } from "./components/SettingsSectionCard";

interface GmailSourceSettingsProps {
  editingConnection: ConnectionEditState;
  onUpdate: ConnectionEditStateUpdater;
  sectionOffset: number;
}

export const GmailSourceSettings: React.FC<GmailSourceSettingsProps> = ({
  editingConnection,
  onUpdate,
  sectionOffset,
}) => {
  const { t } = useTranslation();
  const { boardId } = useParams<{ boardId?: string }>();
  const api = useApi();
  const text = useGmailSourceSettingsText(t);
  const vm = useGmailSourceSettingsViewModel({
    boardId,
    editingConnection,
    onUpdate,
    sourceSettingsApi: getSourceSettingsApiAccessor(api),
    t,
  });

  const [labelQuery, setLabelQuery] = useState("");

  if (!vm.isVisible) {
    return null;
  }

  return (
    <SettingsSectionCard
      sectionOffset={sectionOffset}
      title={t(
        "board.connections.catalogue.sourceSettings.gmail.sectionFilters",
      )}
    >
      <Field
        orientation="horizontal"
        label={text.starredText}
        hint={text.starredHintText}
      >
        <Checkbox
          checked={vm.isStarred}
          onChange={(_, data) => vm.onToggleStarred(Boolean(data.checked))}
        />
      </Field>
      <Field orientation="horizontal" label={text.labelText} hint={text.hintText}>
        <SourceSingleTagPicker
          selectedId={
            vm.selectedLabelId === vm.noneOptionValue
              ? undefined
              : vm.selectedLabelId
          }
          selectedLabel={vm.selectedLabelName}
          query={labelQuery}
          placeholder={text.allLabelsText}
          options={vm.labels.map((label) => ({
            id: label.id,
            label: label.name,
          }))}
          onQueryChange={setLabelQuery}
          onSelect={vm.onSelectLabel}
          clearSelectionText={text.allLabelsText}
          isLoading={vm.isLoading}
        />
        {vm.isLoading && <Text>{text.loadingText}</Text>}
        {vm.hasError && <Text>{text.loadErrorText}</Text>}
      </Field>

      <NewTaskAgeLimitField
        editingConnection={editingConnection}
        onUpdate={onUpdate}
      />
    </SettingsSectionCard>
  );
};
